<template>
  <div>
    <v-row>
      <v-col md="4">
        <v-text-field
          v-model.number="latitude"
          dense
          filled
          hide-details="auto"
          @change="sendLatitude(latitude)"
          @input="updateLatitude"
          :label="
            $t('fleet_management.geofences.create_geofence.labels.latitude')
          "
          placeholder="-90 / 90"
          required
          ref="latitude"
          :rules="[coordinatesRules.latitude]"
        >
          <template v-slot:label>
            <span v-if="latitude" class="blue--text">Latitud</span>
            <span v-else>Latitud</span>
            <span class="asterisc"> *</span>
          </template>
        </v-text-field>
      </v-col>
      <v-col md="4">
        <v-text-field
          v-model.number="longitude"
          @change="sendLongitude(longitude)"
          dense
          filled
          hide-details="auto"
          @input="updateLongitude"
          :label="
            $t('fleet_management.geofences.create_geofence.labels.longitude')
          "
          placeholder="-180 / 180"
          required
          :rules="[coordinatesRules.longitude]"
          ref="longitude"
        >
          <template v-slot:label>
            <span v-if="longitude" class="blue--text">Longitud</span>
            <span v-else>Longitud</span>
            <span class="asterisc"> *</span>
          </template>
        </v-text-field>
      </v-col>
      <v-col md="4">
        <v-text-field
          v-model.number="radius"
          @change="sendRadius(radius)"
          dense
          :error-messages="radiusError"
          filled
          hide-details="auto"
          @input="updateRadius"
          :label="$t('Distancia (metros)')"
          required
          :rules="[radiusRules.required, radiusRules.format, radiusRules.range]"
          ref="radius"
        >
          <template v-slot:label>
            <span v-if="radius" class="blue--text">Distancia (metros)</span>
            <span v-else>Distancia (metros)</span>
            <span class="asterisc"> *</span>
          </template>
        </v-text-field>
      </v-col>
    </v-row>
    <v-row> </v-row>

    <Dialog
      :closeButtonText="dialog.closeButtonText"
      @cancelButtonPressed="showDialog = false"
      :oneButton="dialog.oneButton"
      @oneButtonAction="cancelDialog"
      :oneButtonText="dialog.oneButtonText"
      :showDialog="showDialog"
      @submitButtonPressed="cancelGeofence"
      :submitButtonText="dialog.acceptButtonText"
      :text="dialog.text"
      :type="dialog.type"
    />
  </div>
</template>

<script>
import { GEOFENCE_MIN_RADIUS, GEOFENCE_MAX_RADIUS } from '@leapsight/styleguide'
import { i18n } from '@leapsight/portal'
import { GeofencesHelpers, MapHelpers } from '@/helpers/mainExports.js'
import Dialog from '@/components/dialog/Dialog'
import { mapActions } from 'vuex'

export default {
  name: 'CreateZone',
  components: {
    Dialog
  },
  props: {
    sendLatitude: Function,
    sendLongitude: Function,
    sendRadius: Function,

    zonesList: {
      type: Array
    }
  },
  data() {
    return {
      circle: null,
      coordinatesRules: {
        latitude: value => {
          const regex = /^(\+|-)?(?:90(?:(?:\.0{1,7})?)|(?:[0-9]|[1-8][0-9])(?:(?:\.[0-9]{1,7})?))$/
          const result = regex.test(value)
          //Si no cumple con el formato de coordenada, verifico que sea un número real lo ingresado
          if (!result) {
            const isNumberRegex = /^-?[0-9]\d*(\.\d+)?$/
            const isNumber = isNumberRegex.test(value)
            if (isNumber && value > 90)
              return i18n.t(
                'fleet_management.geofences.create_geofence.rules.max_latitude'
              )
            else if (isNumber && value < -90)
              return i18n.t(
                'fleet_management.geofences.create_geofence.rules.min_latitude'
              )
            else if (!isNumber)
              //No es un número
              return i18n.t('fleet_management.geofences.coordinates.bad_format')
          }
          return result
        },
        longitude: value => {
          const regex = /^(\+|-)?(?:180(?:(?:\.0{1,7})?)|(?:[0-9]|[1-9][0-9]|1[0-7][0-9])(?:(?:\.[0-9]{1,7})?))$/
          const result = regex.test(value)
          if (!result) {
            const isNumberRegex = /^-?[0-9]\d*(\.\d+)?$/
            const isNumber = isNumberRegex.test(value)
            if (isNumber && value > 180)
              return i18n.t(
                'fleet_management.geofences.create_geofence.rules.max_longitude'
              )
            else if (isNumber && value < -180)
              return i18n.t(
                'fleet_management.geofences.create_geofence.rules.min_longitude'
              )
            else if (!isNumber)
              //No es un número
              return i18n.t('fleet_management.geofences.coordinates.bad_format')
          }
          return result
        }
      },
      dialogDefault: {
        text: '',
        closeButtonText: i18n.t('general.dialogComponent.closeButtonText'),
        acceptButtonText: i18n.t('general.dialogComponent.acceptButtonText'),
        type: '',
        loading: false,
        oneButton: false,
        oneButtonText: ''
      },
      dialog: {
        text: '',
        closeButtonText: i18n.t('general.dialogComponent.closeButtonText'),
        acceptButtonText: i18n.t('general.dialogComponent.acceptButtonText'),
        type: '',
        loading: false,
        oneButton: false,
        oneButtonText: ''
      },
      errorName: '',
      geofenceName: '',
      geofenceValid: false,
      geofenceCreatedSuccessful: false,
      longitude: null,
      latitude: null,
      loading: false,
      maxRadius: GEOFENCE_MAX_RADIUS,
      minRadius: GEOFENCE_MIN_RADIUS,
      newGeofenceId: '',
      nameRules: [
        v =>
          !!v ||
          i18n.t('fleet_management.persons.new_person.rules.name_required'),
        v =>
          (v && v.length >= 2 && v.length <= 40) ||
          i18n.t(
            'fleet_management.geofences.create_geofence.rules.character_length'
          )
      ],
      cancelSubmit: false,
      radius: null,
      radiusError: '',
      radiusRules: {
        required: value =>
          !!value ||
          i18n.t(
            'fleet_management.geofences.create_geofence.rules.field_required'
          ),
        range: value => {
          if (value > 1000) return 'El radio debe ser inferior a 1000 mts'
          else if (value < 100) return 'El radio debe ser superior a 100 mts'
          else return !!value
        },
        format: value => {
          const regex = /^\d+$/
          return (
            regex.test(value) ||
            i18n.t('fleet_management.geofences.coordinates.bad_format')
          )
        }
      },
      routeTo: { name: 'geofences-dataTable' },
      showForm: true,
      showDialog: false,
      valid: true
    }
  },
  methods: {
    ...mapActions('ui', ['setSnackbar']),
    cancel() {
      this.showDialog = true
      this.dialog.type = 'warning'
      this.dialog.text = i18n.t(
        'fleet_management.geofences.create_geofence.alerts.card_text_cancel_new_geofence'
      )
      this.dialog.closeButtonText = i18n.t(
        'fleet_management.geofences.create_geofence.alerts.card_cancel_button'
      )
      this.dialog.acceptButtonText = i18n.t(
        'fleet_management.geofences.create_geofence.alerts.card_accept_button'
      )
    },
    cancelDialog() {
      this.showDialog = false
      Object.assign(this.dialog, this.dialogDefault)
    },
    cancelGeofence() {
      this.geofenceCreatedSuccessful = true
      this.$router.push({ name: this.routeTo.name })
    },
    checkName() {
      this.errorName = ''
      this.cancelSubmit = false
      const name = this.geofenceName
      const zones = this.zonesList

      if (zones.length > 0) {
        for (let index = 0; index < zones.length; index++) {
          const element = zones[index]
          if (element.properties.name === name) {
            this.showDialog = true
            this.dialog.type = 'warning'
            this.dialog.text = i18n.t(
              'fleet_management.geofences.create_geofence.errors.name_match'
            )

            this.dialog.oneButton = true
            this.dialog.oneButtonText = i18n.t(
              'general.dialogComponent.acceptButtonText'
            )
            this.errorName = i18n.t(
              'fleet_management.geofences.create_geofence.errors.name_match'
            )
            this.cancelSubmit = true
            break
          }
        }
      }
    },
    async preventDuplicateNameOnSubmit() {
      try {
        const response = await GeofencesHelpers.getGeofences()
        if (!response.hasOwnProperty('error')) {
          const zones = response.map(item => {
            return item.data
          })
          this.zonesList = zones
          this.checkName()
        } else {
          //Mostrar snackbar de error
          this.cancelSubmit = true
          const errorText = GeofencesHelpers.getErrorMessageByCode(
            response.error
          )
          const snackbar = {
            text: errorText
          }
          this.setSnackbar(snackbar)
        }
      } catch (error) {
        //Mostrar snackbar de error
        this.cancelSubmit = true
        const snackbar = {
          text:
            'No se pudo conectar con el servicio por favor intente mas tarde'
        }
        this.setSnackbar(snackbar)
      }
    },
    resetCircleParams() {
      if (this.circle !== null) this.circle.setMap(null)
      this.circle = null
      this.latitude = null
      this.longitude = null
      this.radius = null
      this.radiusError = ''
      this.geofenceValid = false
      this.$refs.form.resetValidation()
    },
    newCircleByInput() {
      this.geofenceValid = true
      const dataToCircle = {
        geometry: {
          coordinates: [this.latitude, this.longitude],
          radius: this.radius
        }
      }
      this.circle = GeofencesHelpers.showGeofenceOnMap(dataToCircle, true)
      GeofencesHelpers.getMap().fitBounds(this.circle.getBounds())
      google.maps.event.addListener(this.circle, 'radius_changed', () => {
        const circleRadius = Number(this.circle.getRadius().toFixed())
        if (circleRadius > GEOFENCE_MAX_RADIUS) {
          this.circle.setRadius(GEOFENCE_MAX_RADIUS)
          this.radius = GEOFENCE_MAX_RADIUS
        } else if (circleRadius < GEOFENCE_MIN_RADIUS) {
          this.circle.setRadius(GEOFENCE_MIN_RADIUS)
          this.radius = GEOFENCE_MIN_RADIUS
        } else this.radius = circleRadius
      })
      google.maps.event.addListener(this.circle, 'center_changed', () => {
        this.latitude = Number(
          this.circle
            .getCenter()
            .lat()
            .toFixed(7)
        )
        this.longitude = Number(
          this.circle
            .getCenter()
            .lng()
            .toFixed(7)
        )
      })
    },
    newCircle(data) {
      const lat = data.latLng.lat()
      const lng = data.latLng.lng()
      this.latitude = Number(lat.toFixed(7))
      this.longitude = Number(lng.toFixed(7))
      this.geofenceValid = true
      // Se calcula los bordes del mapa para poder calcular cuanto mide en metros a lo largo y a lo alto para poder hacer que el  circulo nuevo tenga las dimensiones acordes para entrar en el viewport del mapa y no tener que modificar el zoom del mapa
      const map = GeofencesHelpers.getMap()
      const spherical = google.maps.geometry.spherical
      const bounds = map.getBounds()
      const cor1 = bounds.getNorthEast()
      const cor2 = bounds.getSouthWest()
      const cor3 = new google.maps.LatLng(cor1.lat(), cor2.lng())
      const width = spherical.computeDistanceBetween(cor1, cor3)
      // 0.5 means 50%
      const radius =
        Math.round((width / 2) * 0.5) < 1000
          ? Math.round((width / 2) * 0.5)
          : 1000
      this.radius = radius

      const dataToCircle = {
        geometry: {
          coordinates: [lat, lng],
          radius: radius
        }
      }
      this.circle = GeofencesHelpers.showGeofenceOnMap(dataToCircle, true)

      google.maps.event.addListener(this.circle, 'radius_changed', () => {
        const circleRadius = Number(this.circle.getRadius().toFixed())
        if (circleRadius > GEOFENCE_MAX_RADIUS) {
          this.circle.setRadius(GEOFENCE_MAX_RADIUS)
          this.radius = GEOFENCE_MAX_RADIUS
        } else if (circleRadius < GEOFENCE_MIN_RADIUS) {
          this.circle.setRadius(GEOFENCE_MIN_RADIUS)
          this.radius = GEOFENCE_MIN_RADIUS
        } else this.radius = circleRadius
      })
      google.maps.event.addListener(this.circle, 'center_changed', () => {
        this.latitude = Number(
          this.circle
            .getCenter()
            .lat()
            .toFixed(7)
        )
        this.longitude = Number(
          this.circle
            .getCenter()
            .lng()
            .toFixed(7)
        )
      })
    },
    updateLatitude(val) {
      if (val.indexOf(' ') == -1)
        if (!isNaN(val) && val !== '') {
          this.latitude = Number(parseFloat(val).toFixed(7))
          this.updateMapCircle()
          this.sendLatitude(val)
          // console.log('send latitude')
        }
    },
    updateLongitude(val) {
      if (val.indexOf(' ') == -1)
        if (!isNaN(val) && val !== '') {
          this.longitude = Number(parseFloat(val).toFixed(7))
          this.updateMapCircle()
        }
    },
    updateRadius(val) {
      //Círculo creado?
      if (this.circle !== null) {
        if (
          this.radius >= GEOFENCE_MIN_RADIUS &&
          this.radius <= GEOFENCE_MAX_RADIUS
        )
          this.circle.setRadius(Number(val))
      }
      this.updateMapCircle()
    },
    updateMapCircle() {
      const latitudeHasError = this.$refs.latitude.validate()
      const longitudeHasError = this.$refs.longitude.validate()
      const radiusHasError = this.$refs.radius.validate()
      if (latitudeHasError && longitudeHasError && radiusHasError) {
        this.geofenceValid = true
        //Si los inputs no presentan errores tengo que crear o actualizar el mapa
        //Circulo Creado
        if (this.circle !== null) {
          const center = new google.maps.LatLng(this.latitude, this.longitude)
          this.circle.setCenter(center)
          GeofencesHelpers.getMap().fitBounds(this.circle.getBounds())
        } else {
          //Circulo no creado
          this.newCircleByInput()
        }
      }
    }
  },
  watch: {
    latitude: function(val) {
      this.sendLatitude(val)
    },
    longitude: function(val) {
      this.sendLongitude(val)
    },
     radius: function(val) {
      this.sendRadius(val)
    }
  },

  mounted() {
    this.$nextTick(function() {
      const map = GeofencesHelpers.getMap()
      // console.log(map)
      // Agrego listener
      google.maps.event.addListener(map, 'click', event => {
        // console.log('Adding zone')
        this.newCircle(event)
      })

      // console.log(map)
    })
  },
  beforeDestroy() {
    if (this.circle !== null) {
      this.circle.setMap(null)
    }
    const map = GeofencesHelpers.getMap()
    google.maps.event.clearListeners(map, 'click')
  },

  beforeRouteLeave(to, from, next) {
    if (this.geofenceCreatedSuccessful) {
      next()
    } else {
      this.routeTo = to
      this.showDialog = true
      this.dialog.type = 'warning'
      this.dialog.text = i18n.t(
        'fleet_management.geofences.create_geofence.alerts.card_text_cancel_new_geofence'
      )
      this.dialog.closeButtonText = i18n.t(
        'fleet_management.geofences.create_geofence.alerts.card_cancel_button'
      )
      this.dialog.acceptButtonText = i18n.t(
        'fleet_management.geofences.create_geofence.alerts.card_accept_button'
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.orange--text {
  color: var(--orange) !important;
}
.dialog-attention {
  border: 2px solid var(--orange);
}
.asterisc {
  color: #d90077;
}
</style>
